import React, { useState, useEffect, useContext } from 'react';
import "./Szakjelentesek.css";
import SzakokFinder from '../apis/SzakokFinder';
import { SzakokContext } from '../context/SzakokContext';
import { UserContext } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MissingReportsTable from '../components/MissingReportsTable';
import SideTile from '../components/SideTile';
import Modal from 'react-modal';
import { SZAKJELENTESEK_DETAILS_TRANSLATIONS } from './SzakjelentesekDetailsPageTranslations';

Modal.setAppElement('#root');

const formatDeadline = (endDate) => {
    const date = new Date(endDate);
    if (Number.isNaN(date.getTime())) return null;

    const month = date.toLocaleString('hu-HU', { month: 'long', timeZone: 'UTC' });
    return `${date.getUTCFullYear()}.${month} ${date.getUTCDate()}.`;
};

function Szakjelentesek() {
    const navigate = useNavigate();
    const { szakok, setSzakok } = useContext(SzakokContext);
    const { userData } = useContext(UserContext);
    const [deadline, setDeadline] = useState(null);
    const [showMissingReports, setShowMissingReports] = useState(false);
    const [progressSzak, setProgressSzak] = useState(null);
    const [periodNames, setPeriodNames] = useState([]);

    useEffect(() => {
        if (userData?.role !== 'admin') return;
        let mounted = true;

        const fetchPeriods = async () => {
            try {
                const response = await SzakokFinder.get('/api/periods');
                if (mounted) setPeriodNames((response.data?.data || []).map(period => period.name));
            } catch (err) {
                console.error('Hiba a félévek lekérésekor:', err);
            }
        };

        fetchPeriods();
        return () => { mounted = false; };
    }, [userData?.role]);

    const semesterOptions = [...new Set([
        ...(userData?.role === 'admin' ? periodNames : []),
        ...szakok.map(szak => szak.felev),
    ].filter(Boolean))].sort((a, b) => b.localeCompare(a, 'hu', { numeric: true }));

    useEffect(() => {
        const fetchDeadline = async () => {
            try {
                const response = await SzakokFinder.get('/api/periods/active');
                setDeadline(response.data?.data?.end_date || null);
            } catch (err) {
                if (err.response?.status !== 404) {
                    console.error('Hiba az aktív periódus lekérésekor:', err);
                }
                setDeadline(null);
            }
        };

        fetchDeadline();
    }, []);

    // Egyetlen objektumban tároljuk az összes szűrési feltételt:
    const [filters, setFilters] = useState({
        status: "",
        szakNev: "",
        szakfelelosNev: "",
        tanEv: "",
        kepzesiSzint: "",
    });

    // Rendezési állapot (alapértelmezetten a legfrissebb módosítás van elöl)
    const [sortConfig, setSortConfig] = useState({
        key: 'modositas_datuma',
        direction: 'desc'
    });

    const handleSort = (columnKey) => {
        let direction = 'asc';
        if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key: columnKey, direction });
    };

    const renderSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <span className="sort-icon default">⇅</span>;
        }
        return sortConfig.direction === 'asc' ? (
            <span className="sort-icon active">▲</span>
        ) : (
            <span className="sort-icon active">▼</span>
        );
    };

    useEffect(() => {
        if (!userData || !userData.neptun) return;
    
        const fetchData = async () => {
            try {
                console.log("✅ userData ready, fetching szakok:", userData);
                const response = await SzakokFinder.get("/szakok");
    
                if (response.data?.data?.szakok) {
                    setSzakok(response.data.data.szakok);
                    console.log("✅ Szakok elmentve:", response.data.data.szakok);
                } else {
                    console.warn("⚠️ Az API válasza nem tartalmaz szak adatokat.");
                }
            } catch (err) {
                console.error("❌ Hiba az API hívás során:", err.message);
            }
        };
    
        fetchData();
    }, [userData, setSzakok]);
    
    const handleSzakokSelect = (szakId) => {
        console.log("Kiválasztott szak ID:", szakId);

        const selectedReport = szakok.find((szak) => szak.szak_id === szakId);

        if (userData?.role === 'admin') {
            // 1-es státusz: Még írják az űrlapokat
            if (selectedReport?.status === 1) {
                setProgressSzak(selectedReport);
                return;
            }

            // 2, 3 státuszok: Itt már szabad a belépés a lezáró/véleményező oldalra
            navigate(`/szakjelentesek/${szakId}`);
        } else {
            // Szakfelelős esetén irány az űrlap
            navigate(`/szakjelentes_letrehozas/${szakId}/kepzes-celja`);
        }
    };

    const handleCreateNewReport = () => {
        if (!deadline) return;
        console.log("Új szakjelentés létrehozása");
        navigate('/szakjelentes_letrehozas');
    };

    // A szűrési inputok változásának kezelése
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters({
            ...filters,
            [name]: value,
        });
    };
    
    const [felugyeltSzakok, setFelugyeltSzakok] = useState([]); // A szakfelelős által felügyelt szakok

useEffect(() => {
    const fetchFelugyeltSzakok = async () => {
        if (!userData?.neptun) return; // Ha nincs bejelentkezve, nem futtatjuk

        try {
            const response = await SzakokFinder.get(`/szakfelelosok/szakok/${userData.neptun}`);
            if (response.data && response.data.data) {
                setFelugyeltSzakok(response.data.data.map(szak => szak.szak_kod)); // Csak a szak_kod értékeket mentjük
            }
        } catch (error) {
            console.error("Hiba történt a szakfelelős által felügyelt szakok lekérésekor:", error);
        }
    };

    fetchFelugyeltSzakok();
}, [userData]);


    // A szaklistát a megadott szűrők alapján szűrjük
    const filteredSzakok = szakok.filter((szak) => {
        // Ellenőrizzük, hogy a státusz mező létezik és számérték
        const szakStatusz = typeof szak.status !== "undefined" ? szak.status : 0;
        
        const isSzakfelelosRole = userData?.role === 'szakfelelos';
        const isAdminOrSuperUser = userData?.role === 'admin';
    
        // Ha admin vagy super user, csak azokat látja, amelyek státusza legalább 3 (már beküldött)
        /* if (isAdminOrSuperUser && szakStatusz < 3) {
            return false;
        } */

        // Ha admin/super user és a szakfelelős TLXEEE vagy AEC7DM, csak akkor látható ha a módosító neve megegyezik a bejelentkezett felhasználóval
        if (isAdminOrSuperUser && (szak.szakfelelos_neptun_kodja === 'TLXEEE' || szak.szakfelelos_neptun_kodja === 'AEC7DM' || szak.szakfelelos_neptun_kodja === 'FWDOMU' || szak.szakfelelos_neptun_kodja === 'HPKR9W')) {
            if (szak.modosito_neve !== userData.name) {
                return false;
            }
        }
    
        // Ha szakfelelős, akkor csak azokat a szakokat láthatja, amelyekhez tartozik szakfelelősként
        if (isSzakfelelosRole && !felugyeltSzakok.includes(szak.szak_kod)) {
            return false;
        }

 

        // Státusz szerinti szűrés
        if (filters.status && szak.status_description !== filters.status) {
            return false;
        }
    
        // Szaknév szerinti szűrés (null-safe)
        if (filters.szakNev) {
            const szakName = (szak.name || '').toString();
            if (!szakName.toLowerCase().includes((filters.szakNev || '').toLowerCase())) {
                return false;
            }
        }

        if (filters.szakfelelosNev) {
            const szakfelelosNev = (szak.szakfelelos_neve || '').toString();
            if (!szakfelelosNev.toLowerCase().includes(filters.szakfelelosNev.toLowerCase())) {
                return false;
            }
        }
    
        // Tanév szerinti szűrés
        if (filters.tanEv) {
            const felev = (szak.felev || '').toString();
            if (felev !== filters.tanEv) {
                return false;
            }
        }
    
        // Képzési szint szerinti szűrés (null-safe)
        if (filters.kepzesiSzint) {
            const kepzesi = (szak.kepzesi_szint || '').toString();
            if (!kepzesi.toLowerCase().includes((filters.kepzesiSzint || '').toLowerCase())) {
                return false;
            }
        }
    
        return true;
    });
    
    

    // A szűrt listát rendezhetjük dátum szerint csökkenő sorrendbe
    const sortedSzakok = [...filteredSzakok].sort((a, b) => {
        if (!sortConfig.key) return 0;

        let aValue = a[sortConfig.key] ?? '';
        let bValue = b[sortConfig.key] ?? '';

        // Dátum szerinti rendezés
        if (sortConfig.key === 'modositas_datuma') {
            const timeA = new Date(aValue).getTime();
            const timeB = new Date(bValue).getTime();
            return sortConfig.direction === 'asc' ? timeA - timeB : timeB - timeA;
        }

        // Szöveges (magyar ékezetes ABC) rendezés (pl. név, felev, szakfelelos_neve, modosito_neve)
        const comparison = String(aValue).localeCompare(String(bValue), 'hu', { sensitivity: 'base' });
        return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    const [selectedSzakok, setSelectedSzakok] = useState(new Set());

    const handleCheckboxChange = (szakId) => {
        setSelectedSzakok((prev) => {
            const newSelected = new Set(prev);
            if (newSelected.has(szakId)) {
                newSelected.delete(szakId);
            } else {
                newSelected.add(szakId);
            }
            return newSelected;
        });
    };

    const handleSelectAll = () => {
        if (selectedSzakok.size === sortedSzakok.filter(szak => szak.status !== 1).length) {
            // If all are selected, deselect all
            setSelectedSzakok(new Set());
        } else {
            // Select all visible szakok
            setSelectedSzakok(new Set(sortedSzakok.filter(szak => szak.status !== 1).map(szak => szak.szak_id)));
        }
    };

    const handleFinalizeSelectedReports = async () => {
        if (selectedSzakok.size === 0) return;
    
        const confirmAction = window.confirm("Biztosan véglegesen lezárod a kijelölt szakjelentést/eket?");
        if (!confirmAction) return;
    
        try {
            await Promise.all([...selectedSzakok].map(async (szakId) => {
                const selectedSzak = szakok.find(szak => szak.szak_id === szakId);
                
                if (!selectedSzak) return;
    
                await SzakokFinder.put(`/szakok/${szakId}`, {
                    name: selectedSzak.name, // 🔹 Ne hagyjuk üresen!
                    felev: selectedSzak.felev, // 🔹 Ne hagyjuk üresen!
                    status: 3, // Státusz 3 = véglegesítve
                    modosito_neve: userData?.name || "Ismeretlen felhasználó", // 🔹 Ha nincs név, adjunk egy alapértelmezettet
                    filling_status: selectedSzak.filling_status || 1
                    });
                }));
            
            alert("A kiválasztott szakjelentés/ek véglegesítve lezárva lett/ek!");
            setSelectedSzakok(new Set());
            window.location.reload();
        } catch (error) {
            console.error("Hiba történt a lezárás során:", error);
            alert("Hiba történt a szakjelentések lezárásakor.");
        }
    };

    const handleDownloadSelectedReports = async () => {
        if (selectedSzakok.size === 0) {
            return;
        }

        try {
            const selectedSzakIds = Array.from(selectedSzakok);
            
            if (selectedSzakIds.length === 1) {
                // First, check how many PDFs exist for this szakjelentés
                const pdfListResponse = await SzakokFinder.get(`/api/pdf/szakok/${selectedSzakIds[0]}/pdfs`);
                const pdfs = pdfListResponse.data?.data || [];
                
                if (pdfs.length === 0) {
                    alert('Nincs elérhető PDF fájl ehhez a szakjelentéshez.');
                    return;
                }
                
                if (pdfs.length === 1) {
                    // Only one PDF - download directly
                    const response = await SzakokFinder.get(`${process.env.REACT_APP_API_BASE_URL}/uploads/${pdfs[0].filename}`, {
                        responseType: 'blob'
                    });
                    
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = pdfs[0].filename;
                    
                    document.body.appendChild(link);
                    link.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(link);
                } else {
                    // Multiple PDFs - download as ZIP
                    const response = await SzakokFinder.post('/api/pdf/download-multiple-pdfs', {
                        szakIds: selectedSzakIds
                    }, {
                        responseType: 'blob'
                    });
                    
                    const blob = new Blob([response.data], { type: 'application/zip' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `szakjelentes_${new Date().toISOString().split('T')[0]}.zip`;
                    
                    document.body.appendChild(link);
                    link.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(link);
                }
            } else {
                // Több szakjelentés - letöltés ZIP fájlban
                const response = await SzakokFinder.post('/api/pdf/download-multiple-pdfs', {
                    szakIds: selectedSzakIds
                }, {
                    responseType: 'blob'
                });
                
                const blob = new Blob([response.data], { type: 'application/zip' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `szakjelentesek_${new Date().toISOString().split('T')[0]}.zip`;
                
                document.body.appendChild(link);
                link.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
            }
            
        } catch (error) {
            console.error("Hiba történt a letöltés során:", error);
            alert("Hiba történt a szakjelentések letöltésekor.");
        }
    };
    
    

    const formattedDeadline = deadline ? formatDeadline(deadline) : null;

    return (
    
        
        <div className="szakjelentesek">
            <div className="header-container">
                <h1>Szakjelentések</h1>
                {formattedDeadline && (
                    <p className="deadline-warning">
                        FIGYELEM! A szakjelentés kitöltésének határideje {formattedDeadline}
                    </p>
                )}
                {userData?.role === 'szakfelelos' && (
                    <button className="create-button" onClick={handleCreateNewReport} disabled={!deadline}>
                        <AddIcon className="plus-icon" />
                        Új szakjelentés
                    </button>
                )}
                {userData?.role === 'admin' && (
                    <button
                        type="button"
                        className={`view-toggle-button ${showMissingReports ? 'view-toggle-back' : 'view-toggle-missing'}`}
                        title={showMissingReports ? 'Szakjelentések megtekintése' : 'Hiányzó szakjelentések'}
                        aria-label={showMissingReports ? 'Szakjelentések megtekintése' : 'Hiányzó szakjelentések'}
                        onClick={() => {
                            setSelectedSzakok(new Set());
                            setShowMissingReports(prev => !prev);
                        }}
                    >
                        {showMissingReports ? <ListAltIcon /> : <AssignmentLateIcon />}
                    </button>
                )}
            </div>

            {showMissingReports && userData?.role === 'admin' ? <MissingReportsTable /> : (
            <>
            {/* Szűrési mezők */}
            <div className="filters">
                <div className="filter">
                    <label>Státusz:</label>
                    <select name="status" value={filters.status} onChange={handleFilterChange}>
                        <option value="">Összes</option>
                        <option value="Megkezdett">Megkezdett</option>
                        <option value="Lezárt és véleményezés alatt">Lezárt és véleményezés alatt</option>
                        <option value="Véglegesítve">Véglegesítve</option>
                    </select>
                </div>

                <div className="filter">
                    <label>Szak név:</label>
                    <input
                        type="text"
                        name="szakNev"
                        value={filters.szakNev}
                        onChange={handleFilterChange}
                        placeholder="Keresés szaknév alapján"
                    />
                </div>
                <div className="filter">
                    <label>Szakfelelős neve:</label>
                    <input
                        type="text"
                        name="szakfelelosNev"
                        value={filters.szakfelelosNev}
                        onChange={handleFilterChange}
                        placeholder="Keresés szakfelelős neve alapján"
                    />
                </div>
                <div className="filter">
                    <label>Félév:</label>
                    <select
                        name="tanEv"
                        value={filters.tanEv}
                        onChange={handleFilterChange}
                    >
                        <option value="">Összes</option>
                        {semesterOptions.map(semester => (
                            <option key={semester} value={semester}>{semester}</option>
                        ))}
                    </select>
                </div>
                <div className="filter">
                    <label>Képzési szint:</label>
                    <select
                        name="kepzesiSzint"
                        value={filters.kepzesiSzint}
                        onChange={handleFilterChange}
                    >
                        <option value="">Összes</option>
                        <option value="BA/BSc">BA/BSc</option>
                        <option value="MA/MSc">MA/MSc</option>
                        <option value="Osztatlan képzés">Osztatlan képzés</option>
                        <option value="Felsőoktatási szakképzés">Felsőoktatási szakképzés</option>
                        {/* Tetszés szerint bővíthető további képzési szintekkel */}
                    </select>
                </div>
            </div>
            {userData?.role === 'admin' && selectedSzakok.size > 0 && (
                <>
                    <button 
                        className="szak-button-custom finalize-button floating-bottom-right"
                        onClick={handleFinalizeSelectedReports}
                    >
                        Végleges lezárás
                    </button>
                    <button 
                        className="szak-button-custom download-button floating-bottom-left"
                        onClick={handleDownloadSelectedReports}
                    >
                        <DownloadIcon className="download-icon" />
                        PDF letöltés ({selectedSzakok.size})
                    </button>
                </>
            )}




            {/* A szűrés és rendezés után kapott táblázat */}
            <div className="table-container">
                <table>
                <thead>
                    <tr>
                        {userData?.role === 'admin' && (
                            <th style={{ width: "50px" }}>
                                <input
                                    type="checkbox"
                                    checked={selectedSzakok.size === sortedSzakok.length && sortedSzakok.length > 0}
                                    onChange={handleSelectAll}
                                />
                            </th>
                        )}
                    <th className="sortable-th" onClick={() => handleSort('name')}>
                        <div className="th-content">
                            Szak neve {renderSortIcon('name')}
                        </div>
                    </th>
                    <th>Képzési szint</th>
                        <th className="sortable-th" onClick={() => handleSort('felev')}>
                        <div className="th-content">
                            Félév {renderSortIcon('felev')}
                        </div>
                    </th>
                    <th className="sortable-th" onClick={() => handleSort('status_description')}>
                        <div className="th-content">
                            Státusz {renderSortIcon('status_description')}
                        </div>
                    </th>
                        <th className="sortable-th" onClick={() => handleSort('szakfelelos_neve')}>
                        <div className="th-content">
                            Szakfelelős neve {renderSortIcon('szakfelelos_neve')}
                        </div>
                    </th>
                        <th className="sortable-th" onClick={() => handleSort('modosito_neve')}>
                        <div className="th-content">
                            Módosító neve {renderSortIcon('modosito_neve')}
                        </div>
                    </th>
                        <th className="sortable-th" onClick={() => handleSort('modositas_datuma')}>
                        <div className="th-content">
                            Módosítás dátuma {renderSortIcon('modositas_datuma')}
                        </div>
                    </th>
                    </tr>
                </thead>
                    <tbody>
                        {sortedSzakok.map((szak) => (
                            <tr 
                                key={szak.szak_id} 
                                onClick={(e) => {
                                    if (!e.target.closest('input[type="checkbox"]')) { 
                                        handleSzakokSelect(szak.szak_id);
                                    }
                                }}
                            >
                                {userData?.role === 'admin' && (
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedSzakok.has(szak.szak_id)}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                handleCheckboxChange(szak.szak_id);
                                            }}
                                            disabled={szak.status === 1} // Letiltás 1-es státusz esetén
                                        />
                                    </td>
                                )}
                                <td>{szak.name}</td>
                                <td>{szak.kepzesi_szint}</td>
                                <td>{szak.felev}</td>
                                <td>{szak.status_description}</td>
                                <td>{szak.szakfelelos_neve}</td>
                                <td>{szak.modosito_neve}</td>
                                <td>{new Date(szak.modositas_datuma).toLocaleString("hu-HU", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit"
                                })}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            </>
            )}
            {userData?.role === 'admin' && (
                <Modal
                    isOpen={Boolean(progressSzak)}
                    onRequestClose={() => setProgressSzak(null)}
                    contentLabel="Szakjelentés szakaszainak állapota"
                    className="report-progress-modal"
                    overlayClassName="report-progress-overlay"
                >
                    <div className="report-progress-header">
                        <h2>{progressSzak?.name} – {progressSzak?.kepzesi_szint || 'Nincs képzési szint'} – {progressSzak?.felev}</h2>
                        <button type="button" className="report-progress-close" aria-label="Bezárás" onClick={() => setProgressSzak(null)}>×</button>
                    </div>
                    {progressSzak && (
                        <div className="report-progress-body">
                            <SideTile
                                key={progressSzak.szak_id}
                                id={progressSzak.szak_id}
                                texts={SZAKJELENTESEK_DETAILS_TRANSLATIONS.hu.sidebar}
                                isVisible
                                preview
                            />
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
}

export default Szakjelentesek;
